import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import employeeAttendanceHrAPI from '../../api/employeeAttendanceHrAPI';
import {
  formatDateCompact,
  formatHours,
  formatTimeShort,
  getStatusMeta,
  isAttendanceAbsent,
} from '../../utils/attendanceFormatters';

const API_STATUS_FILTERS = new Set(['all', 'early', 'normal', 'late', 'absent']);

/** Map UI filters to API values; "present" is filtered client-side after fetch. */
function resolveApiStatusFilter(filter) {
  if (!filter || filter === 'all') return 'all';
  if (filter === 'present') return 'all';
  return API_STATUS_FILTERS.has(filter) ? filter : 'all';
}

function applyClientStatusFilter(records, filter) {
  if (filter === 'present') {
    return records.filter((record) => !isAttendanceAbsent(record));
  }
  return records;
}

async function fetchAllAttendanceLines(token, params) {
  const limit = 500;
  let offset = 0;
  let all = [];
  let total = 0;

  do {
    const result = await employeeAttendanceHrAPI.getAttendanceLines(
      { ...params, limit, offset },
      token
    );
    all = all.concat(result.records || []);
    total = result.total ?? all.length;
    offset += limit;
  } while (all.length < total && offset < 5000);

  return all;
}

function getEmptyDetailMessage(summary) {
  if (!summary) {
    return 'No attendance records in this range.';
  }

  const hasActivity =
    Number(summary.present_days) > 0 ||
    Number(summary.absent_days) > 0 ||
    Number(summary.late_days) > 0 ||
    Number(summary.early_days) > 0 ||
    Number(summary.on_time_days) > 0 ||
    Number(summary.normal_days) > 0;

  if (!hasActivity) {
    return 'This employee has no check-ins or attendance activity in the selected period. Summary counts are all zero.';
  }

  return 'No daily attendance lines were returned for this period. The summary may include days that are not stored as line records yet.';
}

export default function HrLinesModal({
  token,
  employeeId,
  employeeName,
  dateFrom,
  dateTo,
  statusFilter,
  summary,
  onClose,
}) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);
        const records = await fetchAllAttendanceLines(token, {
          date_from: dateFrom,
          date_to: dateTo,
          employee_id: Number(employeeId),
          status_filter: resolveApiStatusFilter(statusFilter),
        });
        if (cancelled) return;
        setRecords(applyClientStatusFilter(records, statusFilter));
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load attendance lines');
          setRecords([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token, employeeId, dateFrom, dateTo, statusFilter]);

  const title = employeeName
    ? `${employeeName} — attendance detail`
    : 'Attendance detail';

  return createPortal(
    <div className="ea-dash-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="ea-dash-modal ea-dash-modal--stat ea-hr-lines-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ea-hr-lines-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="ea-dash-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <h3 id="ea-hr-lines-title" className="ea-dash-modal__title">
          {title}
        </h3>
        <p className="ea-dash-modal__subtitle">
          {formatDateCompact(dateFrom)} → {formatDateCompact(dateTo)}
        </p>

        {loading && <p className="ea-hr-lines-modal__loading">Loading…</p>}
        {error && (
          <p className="ea-dash-modal__empty" role="alert">
            {error}
          </p>
        )}
        {!loading && !error && records.length === 0 && (
          <p className="ea-dash-modal__empty">{getEmptyDetailMessage(summary)}</p>
        )}
        {!loading && !error && records.length > 0 && (
          <ul className="ea-dash-stat-modal__list">
            {records.map((record) => {
              const meta = getStatusMeta(record.attendance_status);
              return (
                <li key={record.date} className="ea-dash-stat-modal__item">
                  <span className="ea-dash-stat-modal__date">
                    {formatDateCompact(record.date)}
                  </span>
                  <span className="ea-dash-stat-modal__meta">
                    {record.attendance_status_label || meta.label}
                    {record.check_in
                      ? ` · In ${formatTimeShort(record.check_in)}`
                      : ''}
                    {record.check_out
                      ? ` · Out ${formatTimeShort(record.check_out)}`
                      : ''}
                    {record.worked_hours != null && Number(record.worked_hours) > 0
                      ? ` · ${formatHours(record.worked_hours)}`
                      : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <button type="button" className="ea-btn-submit ea-dash-modal__done" onClick={onClose}>
          Close
        </button>
      </div>
    </div>,
    document.body
  );
}
